import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import {
  BMSM_CART_SOURCE,
  BMSM_GIFT_PEPTIDE,
  BMSM_GIFT_PROPERTY,
  BMSM_GIFT_WATER,
  getGiftSyncActions,
  getQualifyingTotal,
} from '@theme/buy-more-save-more-tiers';

/** @typedef {import('@theme/buy-more-save-more-tiers').BmsmAction} BmsmAction */
/** @typedef {import('@theme/buy-more-save-more-tiers').BmsmCartItem} BmsmCartItem */
/** @typedef {import('@theme/buy-more-save-more-tiers').BmsmTier} BmsmTier */

/**
 * @typedef {object} BmsmRefs
 * @property {HTMLElement} [error] - Gift picker error message.
 */

/** @type {Promise<void>} */
let syncQueue = Promise.resolve();
let cartListenerBound = false;

/**
 * Add or remove GWP gifts after a cart change. Safe to call before the
 * progress bar is in the DOM; config is read from any buy-more-save-more host.
 * @param {{ items?: BmsmCartItem[] } | undefined} [resource]
 * @returns {Promise<void>}
 */
export function queueBmsmSync(resource) {
  bindCartListener();
  if (window.Shopify?.designMode) return Promise.resolve();

  syncQueue = syncQueue.catch(() => undefined).then(() => runGiftSync(resource));
  return syncQueue;
}

function bindCartListener() {
  if (cartListenerBound || typeof document === 'undefined') return;
  cartListenerBound = true;

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    const detail = /** @type {{ detail?: { data?: { source?: string }, resource?: { items?: BmsmCartItem[] } } }} */ (
      event
    ).detail;
    if (detail?.data?.source === BMSM_CART_SOURCE) return;
    queueBmsmSync();
  });
}

/**
 * @returns {{ waterVariantId: number | null, tiers: BmsmTier[] }}
 */
function readBmsmConfig() {
  const el = document.querySelector('buy-more-save-more');
  if (!(el instanceof HTMLElement)) {
    return { waterVariantId: null, tiers: [] };
  }

  const waterVariantId = Number(el.dataset.waterVariantId);
  /** @type {BmsmTier[]} */
  const tiers = [
    { id: 'shipping', amount: Number(el.dataset.tierShipping) || 0, label: 'shipping' },
    { id: BMSM_GIFT_WATER, amount: Number(el.dataset.tierWater) || 0, label: 'water' },
  ];
  const peptideAmount = Number(el.dataset.tierPeptide) || 0;
  if (peptideAmount > 0) {
    tiers.push({ id: BMSM_GIFT_PEPTIDE, amount: peptideAmount, label: 'peptide' });
  }

  return {
    waterVariantId: Number.isFinite(waterVariantId) && waterVariantId > 0 ? waterVariantId : null,
    tiers,
  };
}

/**
 * @param {{ items?: BmsmCartItem[] } | undefined} [resource]
 */
async function runGiftSync(_resource) {
  if (!document.querySelector('buy-more-save-more')) return;

  const { waterVariantId, tiers } = readBmsmConfig();
  const cart = await getCart();
  const items = cart.items ?? [];
  const actions = getGiftSyncActions({
    items,
    qualifying: getQualifyingTotal(items),
    tiers,
    waterVariantId,
  });

  if (actions.length === 0) return;

  await applyGiftActions(actions);
}

/**
 * @param {{ items?: BmsmCartItem[] } | undefined} [resource]
 * @returns {Promise<{ items?: BmsmCartItem[], item_count?: number, sections?: Record<string, string> }>}
 */
async function getCart(resource) {
  if (Array.isArray(resource?.items)) return resource;

  const response = await fetch('/cart.js');
  return response.json();
}

/** @returns {string[]} */
function getSectionIds() {
  /** @type {string[]} */
  const ids = [];
  for (const item of document.querySelectorAll('cart-items-component')) {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      ids.push(item.dataset.sectionId);
    }
  }
  return [...new Set(ids)];
}

/**
 * @param {BmsmAction[]} actions
 */
async function applyGiftActions(actions) {
  const sections = getSectionIds();
  /** @type {Record<string, string> | undefined} */
  let sectionsHtml;
  /** @type {{ item_count?: number, items?: BmsmCartItem[], sections?: Record<string, string> } | undefined} */
  let cart;

  for (const action of actions) {
    if (action.type === 'add') {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', {
          body: JSON.stringify({
            items: [
              {
                id: action.variantId,
                quantity: 1,
                properties: {
                  [BMSM_GIFT_PROPERTY]: action.giftType,
                },
              },
            ],
            sections: sections.join(','),
          }),
        }),
      });
      const data = await response.json();
      if (data.status) {
        throw new Error(data.description || data.message || 'Unable to add gift');
      }
      sectionsHtml = data.sections ?? sectionsHtml;
    } else if (action.type === 'remove') {
      cart = await changeLine(action.key, 0, sections);
      sectionsHtml = cart?.sections ?? sectionsHtml;
    } else {
      cart = await changeLine(action.key, action.quantity, sections);
      sectionsHtml = cart?.sections ?? sectionsHtml;
    }
  }

  const updatedCart = cart?.items ? cart : await getCart();
  document.dispatchEvent(
    new CartUpdateEvent(updatedCart, 'buy-more-save-more', {
      source: BMSM_CART_SOURCE,
      itemCount: updatedCart.item_count,
      sections: sectionsHtml,
    })
  );
}

function closeGiftPickers() {
  for (const picker of document.querySelectorAll('.bmsm__picker')) {
    if (picker instanceof HTMLDetailsElement) picker.open = false;
  }
}

/**
 * @param {string} key
 * @param {number} quantity
 * @param {string[]} sections
 * @returns {Promise<{ item_count?: number, items?: BmsmCartItem[], sections?: Record<string, string> }>}
 */
async function changeLine(key, quantity, sections) {
  const response = await fetch(Theme.routes.cart_change_url, {
    ...fetchConfig('json', {
      body: JSON.stringify({
        id: key,
        quantity,
        sections: sections.join(','),
      }),
    }),
  });
  const data = await response.json();
  if (data.errors || data.status) {
    throw new Error(data.errors || data.description || data.message || 'Unable to update cart');
  }
  return data;
}

bindCartListener();
if (!window.Shopify?.designMode) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => queueBmsmSync(), { once: true });
  } else {
    queueBmsmSync();
  }
}

/**
 * Cart progress bar that keeps stacked GWP gifts in sync with spend tiers.
 * @extends {Component<BmsmRefs>}
 */
class BuyMoreSaveMore extends Component {
  connectedCallback() {
    super.connectedCallback();
    bindCartListener();
  }

  /**
   * Customer picks (or replaces) the $300 peptide gift.
   * @param {Event} event
   */
  chooseGift = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const button = event.target;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const variantId = Number(button.dataset.variantId);
    if (!Number.isFinite(variantId) || variantId <= 0) return;

    button.disabled = true;
    this.#setError('');

    try {
      const cart = await getCart();
      const items = cart.items ?? [];
      const qualifying = getQualifyingTotal(items);
      const peptideTier = readBmsmConfig().tiers.find((tier) => tier.id === BMSM_GIFT_PEPTIDE);
      if (!peptideTier || qualifying < peptideTier.amount) {
        button.disabled = false;
        return;
      }

      /** @type {BmsmAction[]} */
      const actions = [];
      for (const item of items) {
        if (item.properties?.[BMSM_GIFT_PROPERTY] !== BMSM_GIFT_PEPTIDE || !item.key) continue;
        if (Number(item.variant_id) === variantId) {
          closeGiftPickers();
          return;
        }
        actions.push({ type: 'remove', key: item.key });
      }
      actions.push({ type: 'add', variantId, giftType: BMSM_GIFT_PEPTIDE });
      await applyGiftActions(actions);
      closeGiftPickers();
      requestAnimationFrame(closeGiftPickers);
    } catch (error) {
      console.error(error);
      this.#setError(this.dataset.giftError || '');
      button.disabled = false;
    }
  };

  /**
   * Keep the gift button's variant in sync with the select beside it.
   * @param {Event} event
   */
  updateGiftVariant = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const gift = select.closest('.bmsm__gift');
    const button = gift?.querySelector('.bmsm__gift-button');
    if (!(button instanceof HTMLButtonElement)) return;

    button.dataset.variantId = select.value;
  };

  /**
   * @param {string} message
   */
  #setError(message) {
    const error = this.refs.error;
    if (!error) return;

    error.textContent = message;
    error.classList.toggle('hidden', message.trim() === '');
  }
}

if (!customElements.get('buy-more-save-more')) {
  customElements.define('buy-more-save-more', BuyMoreSaveMore);
}
